import { TIERS, TIER_BY_INDEX, TIER_BY_KEY } from './transformConstants';

// Notes in the JSON format may be a single string or an array of lines.
// Internally (and in the API) notes are one string with newline-separated lines.
function notesToString(notes) {
    if (Array.isArray(notes)) return notes.filter((n) => n != null).join('\n');
    return notes == null ? '' : String(notes);
}

// Convert a pasted transform JSON object into an API payload. Throws with a
// human-readable message on malformed input so the UI can surface it.
export function jsonToPayload(json) {
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
        throw new Error('JSON must be a transform object.');
    }
    if (!json.name || !String(json.name).trim()) {
        throw new Error('Transform "name" is required.');
    }

    const abilitiesByTier = json.abilities || {};
    if (typeof abilitiesByTier !== 'object' || Array.isArray(abilitiesByTier)) {
        throw new Error('"abilities" must be an object keyed by tier.');
    }

    const abilities = [];
    for (const [tierKey, list] of Object.entries(abilitiesByTier)) {
        const tier = TIER_BY_KEY[String(tierKey).toLowerCase()];
        if (!tier) throw new Error(`Unknown tier "${tierKey}".`);
        if (!Array.isArray(list)) continue;
        for (const a of list) {
            if (!a || !a.name) continue;
            abilities.push({
                name: String(a.name),
                notes: notesToString(a.notes),
                tier: tier.index,
                isPassive: !!a.isPassive,
                isDefaultUnlocked: !!a.isDefaultUnlocked,
            });
        }
    }

    return {
        name: String(json.name).trim(),
        description: json.description || '',
        costume: json.costume || '',
        abilities,
    };
}

// Convert a loaded transform (API shape) into the external tiered JSON format,
// matching the structure of the sample files (notes collapse to a string when
// single-line, an array when multi-line; static abilities omit the flags).
export function transformToJson(transform) {
    const abilities = {};
    for (const t of TIERS) abilities[t.key] = [];

    for (const a of transform.abilities || []) {
        const tier = TIER_BY_INDEX[a.tier];
        if (!tier) continue;
        const entry = { name: a.name };
        const lines = (a.notes || '').split('\n').filter((l) => l.trim() !== '');
        if (lines.length === 1) entry.notes = lines[0];
        else if (lines.length > 1) entry.notes = lines;
        if (tier.key !== 'static') {
            entry.isDefaultUnlocked = !!a.isDefaultUnlocked;
            entry.isPassive = !!a.isPassive;
        }
        abilities[tier.key].push(entry);
    }

    for (const k of Object.keys(abilities)) {
        if (!abilities[k].length) delete abilities[k];
    }

    return {
        name: transform.name,
        description: transform.description || '',
        costume: transform.costume || '',
        abilities,
    };
}
